import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import useReveal from "../hooks/useReveal";
import { categories } from "../mock";

const CDN = "https://images.squarespace-cdn.com/content/v1/6a94ec3fc462510da664aaac";
const IMGS = {
  portrait: "https://customer-assets-wrfwihn1.emergentagent.net/job_twenty-proof/artifacts/iy3blgqm_Screenshot%202026-09-27%20at%2011.00.53%E2%80%AFPM.png",
  candid: `${CDN}/8df70dba-3862-46b0-834c-63c05cfd3e3b/about+me+1.png`,
  reflective: `${CDN}/2f442b33-01aa-4fa7-a629-5716247d53f2/about+me+3.png`,
  mirror: "/about/still-becoming.webp",
};

const Reveal = ({ children, className = "" }) => {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} ${className}`}>
      {children}
    </div>
  );
};

const Polaroid = ({ src, caption, rotate = 0, className = "", testid, objectPos = "center" }) => (
  <figure
    className={`polaroid bg-white p-3 pb-2 shadow-[0_16px_40px_rgba(0,0,0,0.14)] ${className}`}
    style={{ "--rot": `${rotate}deg`, transform: `rotate(${rotate}deg)` }}
    data-testid={testid}
  >
    <img src={src} alt={caption} className="w-full aspect-[4/5] object-cover" style={{ objectPosition: objectPos }} />
    <figcaption className="font-display italic text-neutral-600 text-center text-lg py-4">
      {caption}
    </figcaption>
  </figure>
);

const Kicker = ({ children }) => (
  <div className="max-w-[440px]">
    <p className="font-sans-ui uppercase tracking-[0.26em] text-base text-neutral-900" style={{ fontWeight: 600 }}>
      {children}
    </p>
    <div className="h-px w-full bg-neutral-600/60 mt-5" />
  </div>
);

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="bg-[#eceae5] min-h-screen" data-testid="about-page">
      <SEO title="About" description="Meet Paris — why she started 20 PROOF, a home for the honest, useful conversations about your twenties." image={IMGS.portrait} type="profile" />
      <Navbar solid />

      {/* Hero — polaroid cluster + intro */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-12 md:pb-16 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
        <Reveal className="order-last md:order-first">
          <div className="flex justify-center">
            <Polaroid src={IMGS.candid} caption="off the clock" rotate={-3} className="w-[85%] max-w-[400px]" testid="about-candid-image" />
          </div>
        </Reveal>
        <Reveal>
          <Kicker>About 20 PROOF</Kicker>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-neutral-900 leading-[1.02] mt-8" style={{ fontWeight: 700 }}>
            Hi, I&rsquo;m Paris.
          </h1>
          <p className="font-display italic text-neutral-600 text-xl md:text-2xl mt-7 max-w-md">
            A 20-something figuring it out in real time &mdash; just like you.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href="https://www.linkedin.com/company/20-proof-media/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-linkedin-company"
              className="group inline-flex items-center gap-2.5 border border-neutral-400 hover:bg-neutral-900 hover:text-white text-neutral-900 px-5 py-2.5 rounded-full transition-all duration-300"
            >
              <Linkedin size={17} />
              <span className="font-sans-ui text-[15px]">20 PROOF</span>
            </a>
            <a
              href="https://www.linkedin.com/in/paris-watson/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-linkedin-personal"
              className="group inline-flex items-center gap-2.5 border border-neutral-400 hover:bg-neutral-900 hover:text-white text-neutral-900 px-5 py-2.5 rounded-full transition-all duration-300"
            >
              <Linkedin size={17} />
              <span className="font-sans-ui text-[15px]">Paris Watson</span>
            </a>
          </div>
        </Reveal>
      </section>

      {/* The short version — text + single polaroid */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
        <Reveal>
          <Kicker>The short version</Kicker>
          <div className="mt-8 flex flex-col gap-6 max-w-[480px]">
            <p className="font-display text-2xl text-neutral-800 leading-relaxed">
              By day I work in finance for live entertainment, helping keep some of the world&rsquo;s
              largest tours on the road &mdash; fast, high-stakes, and never the same two days in a row.
            </p>
            <p className="font-display text-2xl text-neutral-800 leading-relaxed">
              But if I&rsquo;m honest, I&rsquo;m still doing what most of us in our twenties are doing:
              figuring things out as I go.
            </p>
          </div>
        </Reveal>
        <Reveal className="flex justify-center">
          <Polaroid src={IMGS.portrait} caption="me, mid-tour" rotate={4} className="w-[70%] max-w-[320px]" testid="about-portrait-image" objectPos="center 5%" />
        </Reveal>
      </section>

      {/* Why I started — text only for air */}
      <section className="max-w-[860px] mx-auto px-6 py-12 md:py-16 text-center">
        <Reveal>
          <p className="font-sans-ui uppercase tracking-[0.26em] text-base text-neutral-900" style={{ fontWeight: 600 }}>
            Why I started
          </p>
          <div className="h-px w-16 bg-neutral-600/60 mx-auto mt-5 mb-8" />
          <h2 className="font-display text-3xl md:text-5xl text-neutral-900 leading-[1.12]" style={{ fontWeight: 700 }}>
            A place for the conversations I wish I&rsquo;d had sooner.
          </h2>
          <p className="font-display text-2xl text-neutral-800 leading-relaxed mt-8">
            For years I&rsquo;ve mentored college students a few steps behind me, while leaning on my
            own mentors many years ahead. The wisdom we pass down, and the wisdom we reach up for, is
            what actually gets us through this decade. 20 PROOF is my attempt to bottle that.
          </p>
        </Reveal>
      </section>

      {/* Who it's for — polaroid + text */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
        <Reveal className="flex justify-center order-last md:order-first">
          <Polaroid src={IMGS.mirror} caption="still becoming" rotate={-4} className="w-[70%] max-w-[320px]" objectPos="center 20%" />
        </Reveal>
        <Reveal>
          <Kicker>Who it&rsquo;s for</Kicker>
          <div className="mt-8 flex flex-col gap-6 max-w-[480px]">
            <p className="font-display text-2xl text-neutral-800 leading-relaxed">
              This is for every ambitious 20-something trying to figure out the next step, process the
              last one, and balance everything this decade throws at you at once.
            </p>
            <p className="font-display text-2xl text-neutral-800 leading-relaxed">
              For people who want community and growth &mdash; without pretending they have it all
              figured out. Because none of us do. That&rsquo;s kind of the point.
            </p>
            <p className="font-display text-2xl text-neutral-800 leading-relaxed">
              Thanks for being here. Pour yourself something, stay a while, and let&rsquo;s figure it
              out together.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Closing */}
      <section className="py-12 md:py-16">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/topics/${c.slug}`}
                  data-testid={`about-topic-${c.slug}`}
                  className="font-sans-ui text-[15px] text-neutral-900 border border-neutral-400 hover:bg-neutral-900 hover:text-white px-6 py-2.5 rounded-full transition-all duration-300"
                >
                  {c.name.charAt(0) + c.name.slice(1).toLowerCase()}
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/episodes"
                data-testid="about-episodes-cta"
                className="inline-block bg-neutral-900 hover:bg-neutral-700 text-white font-sans-ui text-[15px] px-12 py-4 rounded-full transition-all duration-300 hover:scale-[1.03]"
              >
                Listen to the episodes
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
